import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import LoadingButton from "@mui/lab/LoadingButton";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { sum } from "../../actprep-ui/utils/helpers";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useConfirm } from "material-ui-confirm";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { formatTime } from "../../actprep-ui/utils/formatting";
import { useTimedLoading } from "../../actprep-ui/hooks/feedback/useTimedLoading";
import Polyline from "@mui/icons-material/Polyline";

function aggregateWeekValues(students) {
  students.forEach((student) => {
    student.total_current_weeks = student.student_week_count;
    student.total_program_weeks = sum(
      student.ordered_programs.map(({ week_count }) => week_count)
    );
  });
}

const MIN_GENERATION_TIME_PER_STUDENT = 1;
const MAX_GENERATION_TIME_PER_STUDENT = 1.5;

export default function GenerateMultipleStudentsDataForm({
  students,
  onDataGenerated,
}) {
  const { loading, setLoading, loadingTime } = useTimedLoading();
  const confirm = useConfirm();
  const numStudents = students?.length || 0;
  const minGenTime = formatTime(
    parseInt(MIN_GENERATION_TIME_PER_STUDENT * numStudents)
  );
  const maxGenTime = formatTime(
    parseInt(MAX_GENERATION_TIME_PER_STUDENT * numStudents)
  );
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const studentUUIDs = students.map(({ uuid }) => uuid);
    const response = await api.post(apiUrls.admin.generateProgramData(), {
      student_uuids: studentUUIDs,
    });
    setLoading(false);

    const { results } = response.data;
    const failedResults = results.filter(({ success }) => !success);
    if (!failedResults.length) {
      onDataGenerated(true);
      return;
    }
    confirm({
      hideCancelButton: true,
      confirmationText: "Ok",
      title: "Week Generation Failed",
      description: (
        <>
          <Typography variant="subtitle1">
            Week generation failed for some students. Any students not listed
            here had their weeks generated properly.
          </Typography>
          <List>
            {failedResults.map((failedResult, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={failedResult.student}
                  secondary={`Reason: ${failedResult.reason}`}
                  secondaryTypographyProps={{
                    fontWeight: "bold",
                    ml: 1,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    }).then(() => {
      onDataGenerated(true);
    });
  };
  aggregateWeekValues(students);
  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit}
      sx={{
        height: "80vh",
      }}
    >
      <Typography variant="overline" fontWeight={"bold"} fontSize={"large"}>
        Selected Students
      </Typography>
      <Box component={Paper} p={1}>
        <Box display={"flex"} justifyContent={"flex-start"} gap={1} my={1}>
          <LoadingButton
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disableElevation
            startIcon={<Polyline />}
            loadingPosition="start"
          >
            {loading ? (
              <Typography variant="body1">{formatTime(loadingTime)}</Typography>
            ) : (
              "Generate Weeks"
            )}
          </LoadingButton>
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={"bold"}>
            Estimated generation time: {minGenTime} to {maxGenTime}
          </Typography>
        </Box>
        <Box
          sx={{
            height: "90%",
          }}
        >
          <DataGridPro
            columns={[
              {
                field: "first_name",
                headerName: "Student",
                valueGetter: (val, row) => `${row.first_name} ${row.last_name}`,
                width: 200,
              },
              {
                field: "ordered_programs",
                headerName: "Programs",
                width: 300,
                valueGetter: (orderPrograms) =>
                  orderPrograms
                    .map(
                      (orderedProgram) =>
                        `${orderedProgram.program.title} (${orderedProgram.start_date})`
                    )
                    .join(", "),
              },
              {
                field: "total_current_weeks",
                headerName: "Total Current Weeks",
                width: 150,
              },
              {
                field: "total_program_weeks",
                headerName: "Total Program Weeks",
                width: 150,
              },
            ]}
            rows={students}
          />
        </Box>
      </Box>
    </Box>
  );
}
